import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { getAccessToken } from "../auth/AuthContext";

/** A real-time event pushed from the server hub. Mirrors the server's RealtimeEvent contract. */
export type RealtimeEvent = { type: string; data?: unknown };

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "http://localhost:5218";

// One shared connection for the app. Components subscribe to groups via useRealtimeGroup; the
// lifecycle (start on sign-in, stop on logout) is driven from App.
let connection: HubConnection | null = null;
let startPromise: Promise<void> | null = null;
const reconnectHandlers = new Set<() => void>();

function ensureConnection(): HubConnection {
  if (connection) {
    return connection;
  }

  const conn = new HubConnectionBuilder()
    .withUrl(`${apiBaseUrl}/hubs/notifications`, {
      // Sent as ?access_token= on the handshake; the server's JwtBearer OnMessageReceived lifts it.
      accessTokenFactory: () => getAccessToken() ?? "",
    })
    .withAutomaticReconnect()
    .configureLogging(LogLevel.Warning)
    .build();

  conn.onreconnected(() => {
    console.debug("[realtime] reconnected");
    for (const handler of reconnectHandlers) {
      handler();
    }
  });

  connection = conn;
  return conn;
}

/** Start the connection. Idempotent; concurrent callers share a single start attempt. */
export async function startRealtime(): Promise<void> {
  const conn = ensureConnection();
  if (conn.state !== HubConnectionState.Disconnected) {
    await startPromise;
    return;
  }
  startPromise ??= conn
    .start()
    .then(() => console.debug("[realtime] connected"))
    .catch((err) => console.warn("[realtime] connect failed", err))
    .finally(() => {
      startPromise = null;
    });
  await startPromise;
}

/** Stop and drop the connection (on logout). */
export async function stopRealtime(): Promise<void> {
  // Let any in-flight start finish first so we never abort a negotiation mid-handshake (which
  // React StrictMode's dev-only mount→unmount→remount would otherwise trigger).
  if (startPromise) {
    try {
      await startPromise;
    } catch {
      // start already handles/logs its own failure
    }
  }
  const conn = connection;
  connection = null;
  reconnectHandlers.clear();
  if (conn) {
    await conn.stop();
  }
}

export async function subscribe(group: string): Promise<void> {
  await startRealtime();
  if (connection?.state === HubConnectionState.Connected) {
    await connection.invoke("Subscribe", group);
    console.debug("[realtime] subscribed", group);
  }
}

export async function unsubscribe(group: string): Promise<void> {
  if (connection?.state === HubConnectionState.Connected) {
    try {
      await connection.invoke("Unsubscribe", group);
    } catch {
      // best-effort; the connection may already be gone
    }
  }
}

/** Register a handler for pushed events. Returns an unsubscribe function. */
export function onNotify(handler: (event: RealtimeEvent) => void): () => void {
  const conn = ensureConnection();
  conn.on("notify", handler);
  return () => conn.off("notify", handler);
}

/** Register a handler that runs after an automatic reconnect. Returns an unsubscribe function. */
export function onReconnected(handler: () => void): () => void {
  reconnectHandlers.add(handler);
  return () => {
    reconnectHandlers.delete(handler);
  };
}
